// src/components/EventInfo.jsx
import React, { useState } from "react";
import PropTypes from "prop-types";
import { NavLink } from "react-router-dom";
import { 
  PencilSquareIcon, 
  TrashIcon,
  CalendarDaysIcon,
  UserGroupIcon,
  EnvelopeIcon
} from "@heroicons/react/24/solid";
import EventForm from "./EventForm";

function EventInfo({ event, onEdit, onDelete, hideActions, onEventUpdated }) {
  const [editingData, setEditingData] = useState(null);

  if (!event) return <p>Loading event info...</p>;

  const getStateBadgeStyle = (state) => {
    if (state === "planned") return "bg-blue-100 text-blue-800";
    if (state === "confirmed") return "bg-green-100 text-green-800";
    if (state === "cancelled") return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-800";
  };

  const getStateLabel = (state) => {
    if (state === "planned") return "Planned";
    if (state === "confirmed") return "Confirmed";
    if (state === "cancelled") return "Cancelled";
    return "Unknown";
  };

  const handleEditClick = () => {
    let calculatedDuration = null;
    if (event.start_time && event.end_time) {
      const start = new Date(event.start_time);
      const end = new Date(event.end_time);
      calculatedDuration = (end - start) / (1000 * 60 * 60);
    }

    setEditingData({
      ...event,
      event_duration: calculatedDuration || event.event_duration || 3,
    });
  };

  const handleEditCancel = () => {
    setEditingData(null);
  };

  const handleEditSubmit = async (updatedEvent) => {
    if (onEdit) await onEdit(updatedEvent);
    if (onEventUpdated) await onEventUpdated(updatedEvent);
    setEditingData(null);
  };

  return (
    <div className="space-y-4 p-4">
      <div className="border-b border-gray-200 pb-4 mb-4 pt-2 px-2">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-end">
          <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-4">
            <h2 className="text-2xl font-bold text-gray-900 mb-3 sm:mb-0">{event.name}</h2>
            <div className="flex items-center justify-between sm:justify-start flex-nowrap">
              <div className="flex items-center flex-shrink-0">
                <p className="text-lg font-medium text-gray-600 mr-2">
                  {new Date(event.start_time).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                </p>
                {!hideActions && (
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-sm font-medium ${getStateBadgeStyle(event.state)}`}>
                    {getStateLabel(event.state)}
                  </span>
                )}
              </div>
              {!hideActions && (
                <div className="flex items-center space-x-1 ml-1 flex-shrink-0">
                  <button
                    onClick={handleEditClick}
                    title="Edit Event"
                    className="inline-flex items-center p-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={onDelete}
                    title="Delete Event"
                    className="inline-flex items-center p-1 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {!hideActions && (
        <div className="w-full mt-2 px-2">
          <div className="sm:hidden border-b border-gray-200">
            <nav className="flex w-full" aria-label="Tabs">
              <TabLink label="Event" icon={CalendarDaysIcon} to={`/events/${event.id}`} isMobile={true} />
              <TabLink label="Guest List" icon={UserGroupIcon} to={`/events/${event.id}/guest-list`} isMobile={true} />
              <TabLink label="Invitations" icon={EnvelopeIcon} to={`/events/${event.id}/invitations`} isMobile={true} />
            </nav>
          </div>

          <div className="hidden sm:block border-b border-gray-200">
            <nav className="-mb-px flex space-x-10 pb-1" aria-label="Tabs">
              <TabLink label="Event" icon={CalendarDaysIcon} to={`/events/${event.id}`} isMobile={false} />
              <TabLink label="Guest List" icon={UserGroupIcon} to={`/events/${event.id}/guest-list`} isMobile={false} />
              <TabLink label="Invitations" icon={EnvelopeIcon} to={`/events/${event.id}/invitations`} isMobile={false} />
            </nav>
          </div>
        </div>
      )}

      {editingData && (
        <EventForm
          title="Edit Event"
          initialData={editingData}
          onSubmit={handleEditSubmit}
          onCancel={handleEditCancel}
        />
      )}
    </div>
  );
}

function TabLink({ to, label, icon: Icon, isMobile = false }) {
  if (isMobile) {
    return (
      <NavLink
        to={to}
        end
        className={({ isActive }) =>
          `flex-1 text-center border-b-2 py-4 text-base font-medium ${
            isActive
              ? "border-indigo-500 text-indigo-600"
              : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
          }`
        }
      >
        <div className="flex items-center justify-center">
          <Icon className="h-5 w-5 mr-1" />
          <span>{label}</span>
        </div>
      </NavLink>
    );
  }
  
  return (
    <NavLink
      to={to}
      end
      className={({ isActive }) =>
        `whitespace-nowrap border-b-2 py-5 px-2 text-base font-medium ${
          isActive
            ? "border-indigo-500 text-indigo-600"
            : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
        }`
      }
    >
      <div className="flex items-center">
        <Icon className="h-6 w-6 mr-2" />
        <span>{label}</span>
      </div>
    </NavLink>
  );
}

TabLink.propTypes = {
  to: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  icon: PropTypes.elementType.isRequired,
  isMobile: PropTypes.bool
};



EventInfo.propTypes = {
  event: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string,
    description: PropTypes.string,
    start_time: PropTypes.string,
    end_time: PropTypes.string,
    state: PropTypes.string,
    event_duration: PropTypes.number,
  }).isRequired,
  onEdit: PropTypes.func,
  onDelete: PropTypes.func,
  hideActions: PropTypes.bool,
  onEventUpdated: PropTypes.func,
};

export default EventInfo;